import { useState } from 'react'
import type { AvailableActionOption, Game, Member } from '../../types/game'
import { assetUrl } from '../../utils/assets'
import { ResourceIcon } from '../shared/ResourceIcon'
import { Worker } from '../shared/Worker'

interface MobileEvolutionsPanelProps {
  game: Game
  member: Member
  availableActions: AvailableActionOption[]
  activePanel: string | null
  onEvolve: (evolutionId: number) => void
  onClose: () => void
}

export function MobileEvolutionsPanel({
  game,
  member,
  availableActions,
  activePanel,
  onEvolve,
  onClose,
}: MobileEvolutionsPanelProps) {
  const [selectedEvolutionId, setSelectedEvolutionId] = useState<number | null>(null)
  const player = game.players.find((candidate) => candidate.color === member.color) ?? null
  const evolvedIds = new Set(player?.evolutions.map((evolution) => evolution.id) ?? [])
  const evolvableIds = new Set(
    availableActions
      .filter((action) => action.action === 'evolve' && typeof action.target_id === 'number')
      .map((action) => action.target_id as number)
  )

  return (
    <div id="mobile-evolutions" className={`mobile-panel modal-panel mobile-evolutions${activePanel === 'evolutions' ? ' open' : ''}`}>
      <div className="mobile-panel-shell">
        <div className="mobile-panel-header">
          <div className="mobile-panel-title-main">Evolutions</div>
          <div id="mobile-close-evolutions" className="mobile-panel-close lobby-start" onClick={onClose}>Close</div>
        </div>
        <div className="mobile-market-foragers">
          <div className="mobile-section-label">Evolvers</div>
          <div id="mobile-evolution-worker-pool" className="mobile-market-forager-pool">
            {Object.keys(game.evolvers).map((color) => (
              <Worker key={`evolver-${color}`} color={color as any} />
            ))}
          </div>
        </div>
        <div id="mobile-evolution-list" className="mobile-evolution-list">
          {game.evolutions.map((evolution) => {
            const available = evolvableIds.has(evolution.id)
            const evolved = evolvedIds.has(evolution.id)
            const status = evolved ? 'evolved' : available ? 'available' : 'locked'
            const evolutionWorkers = Object.entries(game.evolvers)
              .filter(([, evolutionId]) => evolutionId === evolution.id)
              .map(([color]) => <Worker key={`${evolution.id}-${color}`} color={color as any} />)

            return (
              <div
                key={evolution.id}
                className={[
                  'mobile-evolution-card',
                  status,
                  selectedEvolutionId === evolution.id ? 'selected' : '',
                ].filter(Boolean).join(' ')}
              >
                <div
                  className="mobile-evolution-tap"
                  onClick={() => setSelectedEvolutionId((current) => (current === evolution.id ? null : evolution.id))}
                >
                  <div className="mobile-evolution-card-head">
                    <div className="mobile-evolution-card-art">
                      <img
                        src={assetUrl('images', 'evolutions', `${evolution.type}.png`)}
                        alt={evolution.name}
                        className="mobile-evolution-card-art-img"
                      />
                    </div>
                    <div className="mobile-evolution-card-main">
                      <div className="mobile-evolution-card-title-row">
                        <div className="mobile-evolution-card-title">{evolution.name}</div>
                        <div className="mobile-evolution-card-status">
                          {evolved ? 'Evolved' : available ? 'Available' : `Level ${evolution.level}`}
                        </div>
                      </div>
                      <div className="mobile-evolution-card-description">{evolution.description}</div>
                      <div className="mobile-evolution-card-effect">{evolution.effect}</div>
                      <div className="mobile-evolution-card-cost">
                        {Object.entries(evolution.cost)
                          .filter(([, amount]) => amount > 0)
                          .map(([resourceOrBrood, amount]) => (
                            <div key={`${evolution.id}-${resourceOrBrood}`} className="mobile-evolution-card-cost-item">
                              <div>{amount}</div>
                              <ResourceIcon resource={resourceOrBrood as 'nectar' | 'pollen' | 'honey' | 'wax' | 'egg' | 'worker'} />
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                  <div className="mobile-evolution-card-footer">
                    <div className="mobile-evolution-worker-row">
                      <ResourceIcon resource="worker" inverted />
                      <div>Level {evolution.level}</div>
                    </div>
                    <div className="mobile-evolution-card-worker-pool">{evolutionWorkers}</div>
                    <div
                      className={`lobby-start mobile-evolution-evolve-button${available ? '' : ' disabled'}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        onEvolve(evolution.id)
                      }}
                    >
                      Evolve
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
